import { useQuery } from '@tanstack/react-query';
import { ArrowRight, TrendingDown, TrendingUp } from 'lucide-react';
import { enumLabel, DataTable, Money, StatusBadge, Spinner, formatCompact, relativeTime, type Column } from '@riskpay/ui-components';
import type { Money as MoneyType, Payment } from '@riskpay/types';
import { api, useAuth } from '../lib/client';

/* ==========================================================================
 *  Merchant dashboard
 *
 *  Composed as three bands, not a grid of floating cards:
 *
 *    1. A single KPI strip divided by hairlines. Four separate boxes each
 *       with their own border is the default every admin template reaches
 *       for, and it fragments the one thing a merchant reads first.
 *    2. Volume over time beside the money buckets, because "how much came in"
 *       and "where is it now" are the same question asked twice.
 *    3. Method performance and recent activity.
 *
 *  Every figure is tabular mono. Nothing is invented — if the API has no data
 *  for a period, the panel says so rather than drawing an empty axis.
 * ========================================================================== */

interface DashboardResponse {
  mode: 'TEST' | 'LIVE';
  kpis: {
    total_volume: MoneyType;
    todays_transactions: number;
    todays_volume: MoneyType;
    success_rate: number;
    todays_success_rate: number;
    net_balance: MoneyType;
    pending_settlements: MoneyType;
  };
  today: {
    total: number;
    succeeded: number;
    failed: number;
    in_flight: number;
    refunded: number;
    disputed: number;
    success_rate: number;
    captured: MoneyType;
    lost: MoneyType;
    in_flight_value: MoneyType;
  };
  balance: Record<'available' | 'pending' | 'reserved' | 'disputed' | 'lifetime_fees', MoneyType>;
  volume_series: { date: string; attempts: number; successes: number; volume: string }[];
  by_method: { method: string; attempts: number; success_rate: number; volume: string }[];
}

const day = (d: string) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });

/* -------------------------------------------------------------------------- */

function KpiStrip({ kpis, loading }: { kpis?: DashboardResponse['kpis']; loading: boolean }) {
  const cells = [
    {
      label: 'Total volume',
      value: <Money amount={kpis?.total_volume.amount} compact />,
      note: 'Lifetime captured',
    },
    {
      label: 'Today',
      value: <span className="numeral">{(kpis?.todays_transactions ?? 0).toLocaleString('en-IN')}</span>,
      note: `${formatCompact(kpis?.todays_volume.amount ?? '0')} today`,
    },
    {
      label: 'Success rate',
      value: <span className="numeral">{(kpis?.success_rate ?? 0).toFixed(1)}%</span>,
      note: `${(kpis?.todays_success_rate ?? 0).toFixed(1)}% today`,
      // Below 85% is where acquirers start asking questions.
      tone: (kpis?.success_rate ?? 100) >= 85 ? 'good' : 'warn',
    },
    {
      label: 'Available balance',
      value: <Money amount={kpis?.net_balance.amount} compact />,
      note: `${formatCompact(kpis?.pending_settlements.amount ?? '0')} settling`,
    },
  ] as const;

  return (
    <section className="panel grid grid-cols-1 divide-y divide-line sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-4">
      {cells.map((cell, i) => (
        <div
          key={cell.label}
          className={`px-5 py-5 ${i > 0 ? 'sm:border-l sm:border-line' : ''} ${
            i === 2 ? 'sm:border-l-0 lg:border-l' : ''
          } ${i >= 2 ? 'sm:border-t sm:border-line lg:border-t-0' : ''}`}
        >
          <p className="meta">{cell.label}</p>
          {loading ? (
            <div className="mt-2.5 h-7 w-24 animate-shimmer rounded bg-[linear-gradient(90deg,#10151F_25%,#1A2130_50%,#10151F_75%)] bg-[length:200%_100%]" />
          ) : (
            <p
              className={`mt-2 text-2xl font-semibold tracking-tight ${
                'tone' in cell && cell.tone === 'warn' ? 'text-warning-text' : 'text-content'
              }`}
            >
              {cell.value}
            </p>
          )}
          <p className="mt-1.5 text-xs text-content-subtle">{cell.note}</p>
        </div>
      ))}
    </section>
  );
}

/**
 * Today, answered as three plain questions.
 *
 * A single "42 transactions today" is the number most dashboards lead with and
 * the least useful one a merchant can be handed: it does not say whether the
 * day is going well. Worked, did not work, still waiting — three counts, each
 * with the money beside it, and a proportion bar so the shape of the day is
 * readable before any of the digits are.
 *
 * Nothing is dramatised. A day with no failures shows a zero in the same
 * typeface as everything else rather than a green flourish, because a merchant
 * checking at 9am should not have to work out whether the design is celebrating.
 */
function TodayBand({ today, loading }: { today?: DashboardResponse['today']; loading: boolean }) {
  const t = today;
  const total = t?.total ?? 0;

  const cells = [
    {
      key: 'succeeded',
      label: 'Succeeded',
      count: t?.succeeded ?? 0,
      money: t?.captured.amount,
      caption: 'money received',
      bar: 'bg-brand-500',
      text: 'text-content',
    },
    {
      key: 'failed',
      label: 'Failed',
      count: t?.failed ?? 0,
      money: t?.lost.amount,
      caption: 'did not complete',
      bar: 'bg-danger',
      text: (t?.failed ?? 0) > 0 ? 'text-danger-text' : 'text-content',
    },
    {
      key: 'in_flight',
      label: 'Still open',
      count: t?.in_flight ?? 0,
      money: t?.in_flight_value.amount,
      caption: 'awaiting the bank',
      bar: 'bg-warning',
      text: (t?.in_flight ?? 0) > 0 ? 'text-warning-text' : 'text-content',
    },
  ] as const;

  return (
    <section className="panel">
      <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-5 py-3.5">
        <h2 className="text-base font-medium">Today</h2>
        <p className="text-xs text-content-subtle">
          {loading ? (
            ' '
          ) : total === 0 ? (
            'No payments attempted yet'
          ) : (
            <>
              <span className="numeral text-content-muted">{total.toLocaleString('en-IN')}</span> attempted ·{' '}
              <span className="numeral text-content-muted">{(t?.success_rate ?? 0).toFixed(1)}%</span> succeeded
            </>
          )}
        </p>
      </header>

      {/* One bar, three segments — the shape of the day before any digits. */}
      {!loading && total > 0 && (
        <div className="flex h-1 w-full overflow-hidden" aria-hidden="true">
          {cells.map((c) => (
            <div key={c.key} className={c.bar} style={{ width: `${(c.count / total) * 100}%` }} />
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 divide-y divide-line sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        {cells.map((cell) => (
          <div key={cell.key} className="px-5 py-5">
            <p className="meta">{cell.label}</p>
            {loading ? (
              <div className="mt-2.5 h-8 w-16 animate-shimmer rounded bg-[linear-gradient(90deg,#10151F_25%,#1A2130_50%,#10151F_75%)] bg-[length:200%_100%]" />
            ) : (
              <p className={`numeral mt-2 text-3xl font-semibold tracking-tight ${cell.text}`}>
                {cell.count.toLocaleString('en-IN')}
              </p>
            )}
            <p className="mt-1.5 text-xs text-content-subtle">
              <Money amount={cell.money} className="text-content-muted" /> {cell.caption}
            </p>
          </div>
        ))}
      </div>

      {/* Only shown when there is something to say. */}
      {!loading && ((t?.refunded ?? 0) > 0 || (t?.disputed ?? 0) > 0) && (
        <p className="border-t border-line px-5 py-2.5 text-xs text-content-subtle">
          Also today:{' '}
          {(t?.refunded ?? 0) > 0 && <span className="numeral">{t?.refunded} refunded</span>}
          {(t?.refunded ?? 0) > 0 && (t?.disputed ?? 0) > 0 && ' · '}
          {(t?.disputed ?? 0) > 0 && <span className="numeral text-warning-text">{t?.disputed} disputed</span>}
        </p>
      )}
    </section>
  );
}

/**
 * Volume chart.
 *
 * Hand-drawn SVG rather than a charting dependency: thirty bars, one series.
 * The grey stub behind each bar is the attempted volume and the emerald is
 * what captured, so the success rate is legible without a legend.
 */
function VolumeChart({ series }: { series: DashboardResponse['volume_series'] }) {
  if (series.length === 0) {
    return (
      <div className="flex h-52 flex-col items-center justify-center gap-1.5 px-6 text-center">
        <p className="text-sm text-content-muted">No volume in the last 30 days</p>
        <p className="max-w-xs text-xs leading-relaxed text-content-faint">
          Transactions appear here within a minute of being captured. Switch modes if you are looking for live traffic.
        </p>
      </div>
    );
  }

  const max = series.reduce((m, d) => Math.max(m, Math.round(Number(d.volume))), 1);

  return (
    <div className="px-5 pb-5">
      {/* justify-start + a capped bar width: with flex-1 alone a single day of
          data stretches into one full-width block, which reads as a broken
          chart rather than as one bar. */}
      <div className="flex h-52 items-end justify-start gap-[3px]">
        {series.map((d) => {
          const rupees = Math.round(Number(d.volume));
          const height = Math.max((rupees / max) * 100, 2);
          const rate = d.attempts > 0 ? d.successes / d.attempts : 0;
          return (
            <div
              key={d.date}
              className="group relative flex h-full max-w-[2.25rem] flex-1 items-end"
              title={`${day(d.date)} · ${formatCompact(d.volume)} · ${d.successes}/${d.attempts} captured`}
            >
              <div
                className="w-full rounded-[2px] bg-line-strong transition-colors group-hover:bg-line-bold"
                style={{ height: `${height}%` }}
              >
                <div
                  className="w-full rounded-[2px] bg-brand-600 transition-colors group-hover:bg-brand-500"
                  style={{ height: `${rate * 100}%`, marginTop: `${(1 - rate) * 100}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex justify-between border-t border-line pt-2.5 text-xs text-content-faint">
        <span>{day(series[0]!.date)}</span>
        <span className="numeral">Peak {formatCompact(max)}</span>
        <span>{day(series[series.length - 1]!.date)}</span>
      </div>
    </div>
  );
}

function BalanceBreakdown({ balance }: { balance?: DashboardResponse['balance'] }) {
  const rows = [
    ['Available', balance?.available, 'Settleable now', 'text-content'],
    ['Pending', balance?.pending, 'Inside the settlement window', 'text-content-muted'],
    ['Reserved', balance?.reserved, 'Rolling reserve held back', 'text-content-muted'],
    ['Disputed', balance?.disputed, 'Locked against chargebacks', 'text-content-muted'],
    ['Lifetime fees', balance?.lifetime_fees, 'MDR + GST paid to date', 'text-content-subtle'],
  ] as const;

  return (
    <dl className="divide-y divide-line-subtle">
      {rows.map(([label, value, hint, tone]) => (
        <div key={label} className="flex items-start justify-between gap-4 px-5 py-3.5">
          <div className="min-w-0">
            <dt className="text-sm text-content">{label}</dt>
            <dd className="mt-1 text-xs text-content-faint">{hint}</dd>
          </div>
          <dd className="shrink-0">
            <Money amount={value?.amount} className={`text-sm ${tone}`} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

/* -------------------------------------------------------------------------- */

export function Dashboard({ onViewAll }: { onViewAll: () => void }) {
  const mode = useAuth((s) => s.session?.mode ?? 'TEST');

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', mode],
    queryFn: () => api.get<DashboardResponse>('/dashboard'),
    refetchInterval: 60_000,
  });

  const recent = useQuery({
    queryKey: ['transactions', 'recent', mode],
    queryFn: () => api.get<{ data: Payment[] }>('/transactions', { limit: 8 }),
    refetchInterval: 30_000,
  });

  const columns: Column<Payment>[] = [
    {
      key: 'id',
      header: 'Transaction',
      render: (t) => (
        <div>
          <p className="numeral text-xs text-content">{t.id}</p>
          {t.merchant_order_id && <p className="numeral mt-1 text-xs text-content-faint">{t.merchant_order_id}</p>}
        </div>
      ),
    },
    {
      key: 'method',
      header: 'Method',
      render: (t) => <span className="text-sm text-content-muted">{enumLabel(t.payment_method)}</span>,
    },
    { key: 'status', header: 'Status', render: (t) => <StatusBadge status={t.status} /> },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (t) => <Money amount={t.amount} currency={t.currency} className="text-sm" />,
    },
    {
      key: 'net',
      header: 'Net',
      align: 'right',
      render: (t) => <Money amount={t.net_amount.amount} className="text-sm text-content-muted" />,
    },
    {
      key: 'when',
      header: 'When',
      align: 'right',
      render: (t) => <span className="text-xs text-content-subtle">{relativeTime(t.created_at)}</span>,
    },
  ];

  return (
    <div className="space-y-5">
      {/* Today first. The lifetime figures below matter, but nobody opens a
          payments dashboard to find out how last year went. */}
      <TodayBand today={data?.today} loading={isLoading} />
      <KpiStrip kpis={data?.kpis} loading={isLoading} />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <section className="panel">
          <header className="flex items-center justify-between gap-4 border-b border-line px-5 py-3.5">
            <h2 className="text-base font-medium">Volume · last 30 days</h2>
            <p className="hidden items-center gap-3 text-xs text-content-faint sm:flex">
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-[2px] bg-brand-600" aria-hidden="true" />
                captured
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-[2px] bg-line-strong" aria-hidden="true" />
                attempted
              </span>
            </p>
          </header>
          {isLoading ? (
            <div className="flex h-52 items-center justify-center">
              <Spinner className="h-5 w-5 text-content-faint" />
            </div>
          ) : (
            <VolumeChart series={data?.volume_series ?? []} />
          )}
        </section>

        <section className="panel">
          <header className="border-b border-line px-5 py-3.5">
            <h2 className="text-base font-medium">Balance</h2>
          </header>
          <BalanceBreakdown balance={data?.balance} />
        </section>
      </div>

      <section className="panel">
        <header className="border-b border-line px-5 py-3.5">
          <h2 className="text-base font-medium">Success rate by method · last 30 days</h2>
        </header>
        <ul className="divide-y divide-line-subtle">
          {(data?.by_method ?? []).map((row) => {
            const healthy = row.success_rate >= 85;
            return (
              <li key={row.method} className="flex items-center gap-4 px-5 py-3.5">
                <span className="w-32 shrink-0 truncate text-sm text-content">{enumLabel(row.method)}</span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-line">
                  <div
                    className={`h-full rounded-full ${
                      healthy ? 'bg-brand-500' : row.success_rate >= 70 ? 'bg-warning' : 'bg-danger'
                    }`}
                    style={{ width: `${row.success_rate}%` }}
                  />
                </div>
                <span className="numeral flex w-20 shrink-0 items-center justify-end gap-1 text-sm text-content-muted">
                  {healthy ? (
                    <TrendingUp className="h-3.5 w-3.5 text-brand-500" aria-hidden="true" />
                  ) : (
                    <TrendingDown className="h-3.5 w-3.5 text-warning" aria-hidden="true" />
                  )}
                  {row.success_rate.toFixed(1)}%
                </span>
                <span className="numeral w-24 shrink-0 text-right text-sm text-content-subtle">
                  {formatCompact(row.volume)}
                </span>
              </li>
            );
          })}
          {(data?.by_method ?? []).length === 0 && !isLoading && (
            <li className="px-5 py-10 text-center text-sm text-content-faint">
              No transactions in this period.
            </li>
          )}
        </ul>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-medium">Recent transactions</h2>
          <button type="button" onClick={onViewAll} className="btn btn-ghost btn-sm">
            View all
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
        <DataTable columns={columns} rows={recent.data?.data ?? []} rowKey={(t) => t.id} loading={recent.isLoading} />
      </section>
    </div>
  );
}
